import { Navigate, useLocation } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { routes } from 'src/Router';
import { LoadingScreen } from 'src/components/loading-screen';
import { Analytics } from 'src/services/analytics';
import {
  type LocationData,
  forceStartTemporarySession,
  getCurrentSession,
  getLastSession,
  getSessionByUserId,
  getSessionSlug,
} from 'src/state';
import { useAuthContext } from './useAuthContext';

export const AfterSignInGuard = () => {
  const { isAuthenticated, isInitialized, user } = useAuthContext();
  const { search } = useLocation();
  const [searchParams] = useSearchParams();

  if (!isInitialized) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated || !user) {
    return <Navigate to={`${routes.auth.login}${search}`} replace />;
  }

  const redirectPath = (section: LocationData) => {
    const slug = getSessionSlug(section);
    const fallbackPath = `chat`;
    const path = section.lastPath || fallbackPath;
    const segments = [slug, path].join('/');
    return `/${segments}${search}`;
  };

  const inviteToken = searchParams.get('workspaceInvite');

  if (inviteToken) {
    const nextSearch = new URLSearchParams(search);
    nextSearch.delete('workspaceInvite');
    forceStartTemporarySession();
    return (
      <Navigate to={`/workspaceJoining/${inviteToken}?${nextSearch}`} replace />
    );
  }

  if (!user.wizard.completedWizard) {
    return <Navigate to={`${routes.wizard.root}${search}`} replace />;
  }

  const session = getCurrentSession();

  if (session) {
    //? track session only here because the cases below will redirect to this case anyway
    Analytics.identifySession(user, session);
    return <Navigate to={redirectPath(session)} replace />;
  }

  const lastSession = getLastSession();
  if (lastSession) {
    window.location.replace(redirectPath(lastSession));
    return null;
  }

  const userSession = getSessionByUserId(user._id);
  if (userSession) {
    window.location.replace(redirectPath(userSession));
    return null;
  }

  return <Navigate to={`${routes.auth.login}${search}`} replace />;
};
